
import { useState } from "react";
import { Service } from "@/lib/types/services";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ServiceForm } from "./ServiceForm";
import { 
  Command, 
  CommandEmpty, 
  CommandGroup, 
  CommandInput, 
  CommandItem, 
  CommandList 
} from "@/components/ui/command";
import { useServices } from "@/hooks/useServices";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";

interface ServiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service?: Service;
  onSuccess?: (service: Service) => void;
  // Add a contextType prop to identify where the modal is being used
  contextType?: "contract-edit" | "standalone";
  // Add contractId for directly associating the service with a contract
  contractId?: string;
}

export function ServiceModal({
  open,
  onOpenChange,
  service,
  onSuccess,
  contextType = "standalone",
  contractId,
}: ServiceModalProps) {
  const [mode, setMode] = useState<"search" | "create">(service ? "create" : "search");
  const { useServicesQuery } = useServices();
  const { data: services = [] } = useServicesQuery();

  const handleSuccess = (savedService: Service) => {
    // Reset mode back to search after successful creation
    setMode("search");
    
    // Then call the onSuccess callback if provided
    if (onSuccess) {
      onSuccess(savedService);
    }
  };

  const handleCancel = () => {
    // Reset mode back to search on cancel
    setMode("search");
    onOpenChange(false);
  };

  const handleServiceSelect = (selectedService: Service) => {
    if (onSuccess) {
      onSuccess(selectedService);
    }
    onOpenChange(false);
  };

  const handleCreateNew = () => {
    setMode("create");
  };
  
  // Reset mode when modal is closed
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset the mode to search when closing the modal
      setMode(service ? "create" : "search");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" 
              ? (service ? "Edit Service" : "Create New Service") 
              : "Add Service to Contract"}
          </DialogTitle>
        </DialogHeader>
        
        {mode === "search" ? (
          <div className="space-y-4">
            <Command className="rounded-lg border shadow-md">
              <CommandInput placeholder="Search services..." />
              <CommandList>
                <CommandEmpty>No services found.</CommandEmpty>
                <CommandGroup>
                  {services.map((s) => (
                    <CommandItem 
                      key={s.id}
                      onSelect={() => handleServiceSelect(s)}
                      className="flex justify-between cursor-pointer"
                    >
                      <div>
                        <span className="font-medium">{s.name}</span>
                        <span className="text-sm text-muted-foreground ml-2">({s.service_type})</span>
                      </div>
                      <span className="text-sm">{s.cost} {s.currency?.code}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
            
            <div className="flex justify-end">
              <Button onClick={handleCreateNew} className="flex items-center">
                <PlusCircle className="mr-2 h-4 w-4" />
                Create New Service
              </Button>
            </div>
          </div>
        ) : (
          <ServiceForm
            service={service}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            contextType={contextType}
            contractId={contractId}
            // Add preventNavigation prop to indicate we should stay on the current page
            preventNavigation={contextType === "contract-edit"}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
